import { Component, ChangeDetectorRef, ElementRef, EventEmitter, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { CanComponentDeactivate, UnsavedChangesGuard } from 'core/unsaved-changes/unsaved-changes-guard.class';
import { UtilsService } from 'core/utils/utils.service';
import { forkJoin, isObservable } from 'rxjs';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { DashboardService } from 'src/app/modules/crm/dashboard/services/dashboard.service';
import { Customer } from 'src/app/modules/shared/entities/customer.class';
import { ContactsDashboardComponent } from 'src/app/modules/crm/contacts/contacts-dashboard/contacts-dashboard.component';
import {
  RECURRENCE_DAYS,
  RecurrenceDayField,
  Touchpoint,
  TouchpointAttachment,
  TouchpointComment,
  TouchpointSaveResult,
  TouchpointTemplateValues
} from '../entities/touchpoints-entity';
import { TouchpointsService } from '../services/touchpoints.service';

@Component({
  selector: 'app-edit-touchpoint',
  templateUrl: './edit-touchpoint.component.html',
  styleUrls: ['./edit-touchpoint.component.scss'],
  standalone: false
})

export class EditTouchpointComponent implements OnInit, CanComponentDeactivate {
  private static readonly NON_WHITESPACE_PATTERN = /^(\s|\S)*(\S)+(\s|\S)*$/;
  private static readonly TOUCHPOINT_TYPE = { Call: 2, Task: 4 } as const;

  private static contactMustBeSelectedValidator(control: AbstractControl): ValidationErrors | null {
    return Array.isArray(control.value) || !control.value ? null : { invalidContact: true };
  }

  appStrings: AppLocalization;
  dateTimeFormat: string;
  dsDateFormat: string;
  customerIdParam: string;
  leadIdParam: string;
  touchpointIdParam: string;
  pageTitle: string;

  isNewTouchpoint = true;
  templateValues: TouchpointTemplateValues;
  touchpointTypes = [];
  alarmTypes = [];
  isLoading = false;
  hasContacts = false;
  isLoadingContacts = true;
  isLoadingCustomers = false;
  contactOptionsReady = true;
  isTouchpointLoaded = false;
  isMobile = false;
  // Both flags mean the record came from Outlook and has no CRM row yet, but they lead opposite ways:
  // isMergingToCrm creates a CRM touchpoint and drops the Outlook copy, isOutlookOnly edits it in place.
  isMergingToCrm = false;
  isOutlookOnly = false;
  accountManagersLoaded = false;
  isCurrentUserAccountManager = false;
  private employeeId: string | undefined;
  isAccountDashboard = false;
  deleteDialogOpen$ = new EventEmitter<boolean>();
  dialogDeleteButtons: any[] = [
    {
      label: 'Cancel',
      type: 'secondary',
    },
    {
      label: 'Delete',
      type: 'primary',
      onclick: () => {
        this.isLoading = true;
        this.touchpointsService.deleteTouchpoint(this.touchpointIdParam, this.applyToSeries, null, () => {
          this.dialogRef.close(true);
        }, this.isOutlookOnly, this.employeeId);
      }
    }
  ];

  touchpointContactCtrl = new UntypedFormControl([], [Validators.required, EditTouchpointComponent.contactMustBeSelectedValidator]);
  contactOptions: any[] = [];
  deletedContacts: any[] = [];

  private readonly recurrenceConfig = {
    patterns: {
      daily: { value: 'Daily', label: 'Day' },
      weekly: { value: 'Weekly', label: 'Week' },
      absoluteMonthly: { value: 'AbsoluteMonthly', label: 'Month' },
    },
    days: RECURRENCE_DAYS,
  } as const;

  readonly weekDays = this.recurrenceConfig.days.map(({ value }) => ({ value }));

  get recurrencePatternOptions(): { value: string, label: string }[] {
    const templatePatterns = this.templateValues?.RecurrencePattern
      ? Object.values(this.templateValues.RecurrencePattern)
      : Object.values(this.recurrenceConfig.patterns).map(pattern => pattern.value);

    return Object.values(this.recurrenceConfig.patterns)
      .filter(pattern => templatePatterns.includes(pattern.value))
      .map(pattern => ({ value: pattern.value, label: pattern.label }));
  }

  selectedDays: string[] = [];
  seriesMasterId: string | null = null;
  private wasOriginallyRecurring = false;
  private applyToSeries = false;
  private recurrenceValidationAttempted = false;
  private minRecurrenceEndDate: string | null = null;

  recurrenceEndMinDate: Date | null = null;

  attachments: TouchpointAttachment[] = [];
  isAttachmentLoading = false;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  touchpointNotes: TouchpointComment[] = [];
  noteCtrl = new UntypedFormControl('');

  accountManagerOptions: { value: string, label: string }[] = [];
  customerOptions: { value: string, label: string, description?: string, isLead?: boolean }[] = [];
  private accountManagers: AccountManager[] = [];
  private customers: Customer[] = [];
  private pendingNewContactBaseIds: Set<string> | null = null;

  payload: Touchpoint;
  formFields = {
    contacts: ['', Validators.required],
    touchpointType: ['', Validators.required],
    subject: ['', [Validators.required, Validators.maxLength(255), Validators.pattern(EditTouchpointComponent.NON_WHITESPACE_PATTERN)]],
    location: ['', [Validators.required, Validators.maxLength(255)]],
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    startTime: ['', Validators.required],
    endTime: ['', Validators.required],
    alarmType: [6, Validators.required],
    notes: ['', [Validators.required, Validators.maxLength(2000), Validators.pattern(EditTouchpointComponent.NON_WHITESPACE_PATTERN)]],
    accountManager: ['', Validators.required],
    customerBpNumber: ['', Validators.required],
    lastTouchpoint: [{ value: '', disabled: true }],
    recurring: [false],
    recurrenceInterval: [1, [Validators.min(1)]],
    recurrencePattern: [this.recurrenceConfig.patterns.weekly.value],
    dayOfMonth: [1],
    recurrenceEndDate: [''],
  };

  touchpointForm = this.fb.group(this.formFields);

  constructor(
    public dialogRef: MatDialogRef<EditTouchpointComponent>,
    private touchpointsService: TouchpointsService,
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private notificationService: NotificationService,
    private utilsService: UtilsService,
    public deviceService: DeviceDetectorService,
    private dashboardService: DashboardService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
    private unsavedChangesGuard: UnsavedChangesGuard,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.dialogRef.disableClose = true;
    this.isMobile = this.deviceService.isMobile();
    this.isCurrentUserAccountManager = this.appInfoService.isAccountManager();
    this.initRecurrenceDayControls();
    this.setDefaultValues();
    this.loadAccountManagers();

    this.touchpointsService.contactList$.subscribe(value => {
      if (value.length) {
        this.hasContacts = true;
      } else {
        this.touchpointContactCtrl.setErrors({ required: true });
        this.hasContacts = false;
      }

      this.contactOptions = this.utilsService.mapObjectToSelect(value, 'id', 'name');
      this.isLoadingContacts = false;
      this.touchpointContactCtrl.enable();
      this.selectPendingNewContact(value);
      this.contactOptionsReady = false;
      this.cdr.detectChanges();
      this.contactOptionsReady = true;
      this.cdr.detectChanges();
    });

    this.templateValues = this.touchpointsService.templateValues;

    this.touchpointForm.controls.startDate.valueChanges.subscribe(() => {
      this.validateDateTimeRelationship();
      this.updateRecurrenceEndMinDate();
    });
    this.touchpointForm.controls.endDate.valueChanges.subscribe(() => {
      setTimeout(() => {
        this.validateDateTimeRelationship();
      });
    });
    this.touchpointForm.controls.recurrencePattern.valueChanges.subscribe(() => {
      this.updateDayOfMonthValidators();
    });

    if (this.templateValues) {
      this.initData();
    } else {
      this.touchpointsService.touchpointTemplateValues$.subscribe(value => {
        this.templateValues = value;
        this.initData();
      });
    }
  }

  setDefaultValues() {
    this.appStrings = this.appInfoService.appStrings;
    this.dateTimeFormat = this.appInfoService.dateTimeFormat;
    this.dsDateFormat = this.appInfoService.dsDateFormat;
    this.customerIdParam = this.data.customerId;
    this.leadIdParam = this.data.leadId;
    this.touchpointIdParam = this.data.touchpointId;
    this.employeeId = this.data.employeeId ?? undefined;
    this.pageTitle = this.touchpointIdParam ? this.appStrings.label_edit : this.appStrings.label_new;
    this.pageTitle += ` ${this.appStrings.label_touchpoint}`;
    this.isMergingToCrm = Boolean(this.data.isMergingToCrm);
    this.applyToSeries = Boolean(this.data.updateRecurrence);
    this.isAccountDashboard = Boolean(this.data.isAccountDashboard);
    this.isOutlookOnly = Boolean(this.data.isOutlookOnly);

    if (this.isMergingToCrm) {
      this.disableRecurrenceControls();
    }

    if (this.isOutlookOnly) {
      this.clearAccountAndContactValidators();
      this.isLoadingContacts = false;
      return;
    }

    if (this.customerIdParam) {
      this.touchpointsService.getContactsForCustomer(this.customerIdParam);
      this.touchpointForm.controls.customerBpNumber.setValue(this.customerIdParam);
    } else if (this.leadIdParam) {
      this.touchpointsService.getContactsForLead(this.leadIdParam);
      this.touchpointForm.controls.customerBpNumber.setValue(this.leadIdParam);
    } else {
      this.isLoadingContacts = false;
    }
  }

  private disableRecurrenceControls() {
    const { recurring, recurrenceInterval, recurrencePattern, dayOfMonth, recurrenceEndDate } = this.touchpointForm.controls;
    [recurring, recurrenceInterval, recurrencePattern, dayOfMonth, recurrenceEndDate].forEach(control =>
      control.disable({ emitEvent: false })
    );
  }

  private clearAccountAndContactValidators() {
    const { customerBpNumber, contacts } = this.touchpointForm.controls;
    customerBpNumber.clearValidators();
    customerBpNumber.updateValueAndValidity();
    contacts.clearValidators();
    contacts.updateValueAndValidity();
    this.touchpointContactCtrl.clearValidators();
    this.touchpointContactCtrl.updateValueAndValidity();
  }

  private initData() {
    setTimeout(() => {
      this.touchpointTypes = this.utilsService.mapNumberKeyValueOptions(this.templateValues.TouchpointType);
      this.alarmTypes = this.utilsService.mapNumberKeyValueOptions(this.templateValues.AlarmType);
      if (this.templateValues.RecurrencePattern) {
        this.touchpointForm.controls.recurrencePattern.setValue(
          this.recurrenceConfig.patterns.weekly.value
        );
      }
      if (this.touchpointIdParam) {
        const getTouchpointsCall =
          this.isMergingToCrm || this.isOutlookOnly
            ? this.touchpointsService.outlookTouchpointDetails(this.touchpointIdParam, this.employeeId)
            : this.touchpointsService.getTouchpoint(this.touchpointIdParam, this.employeeId);

        getTouchpointsCall.subscribe((touchpoint: Touchpoint) => {
          this.isNewTouchpoint = false;
          const startDate = new Date(touchpoint.startDate);
          const endDate = new Date(touchpoint.endDate);

          const typeLabel = this.templateValues.TouchpointType[touchpoint.touchpointType];
          if (touchpoint.subject && typeLabel) {
            this.pageTitle = `${touchpoint.subject} (${typeLabel})`;
          }

          const rawAccountManagerValue =
            touchpoint.accountManagerId ?? this.data.accountManagerId ?? this.touchpointForm.controls.accountManager.value ?? '';
          const customerId = touchpoint.customerId === this.appInfoService.emptyGuid ? null : touchpoint.customerId;
          this.touchpointForm.patchValue({
            contacts: touchpoint.contacts,
            touchpointType: touchpoint.touchpointType,
            subject: touchpoint.subject,
            location: touchpoint.location,
            startDate,
            endDate,
            startTime: startDate,
            endTime: endDate,
            alarmType: touchpoint.alarmType,
            notes: touchpoint.notes,
            accountManager: rawAccountManagerValue,
            customerBpNumber: customerId ?? touchpoint.leadId ?? this.customerIdParam ?? this.leadIdParam ?? '',
            lastTouchpoint: touchpoint.lastTouchpoint ?? '',
            recurring: touchpoint.isRecurring ?? false,
            recurrenceInterval: touchpoint.recurrenceInterval ?? 1,
            recurrencePattern: touchpoint.recurrencePattern ?? this.recurrenceConfig.patterns.weekly.value,
            dayOfMonth: touchpoint.dayOfMonth || 1,
            recurrenceEndDate: this.getRecurrenceEndDate(touchpoint.recurrenceEndDate),
          });
          this.minRecurrenceEndDate = this.utilsService.isMinDate(touchpoint.recurrenceEndDate) ? touchpoint.recurrenceEndDate : null;
          this.selectedDays = this.getSelectedDaysFromTouchpoint(touchpoint);
          this.seriesMasterId = touchpoint.seriesMasterId ?? null;
          this.wasOriginallyRecurring = touchpoint.isRecurring ?? false;
          if (touchpoint.isRecurring && !this.isSeriesOccurrenceEdit) {
            this.onRecurringChange();
          }
          if (!this.isMergingToCrm) {
            this.loadNotes();
          }
          this.attachments = touchpoint.attachments ?? [];
          // Set the selected contacts in the control - extract just the IDs
          const contactIds = (touchpoint.contacts || []).map(contact => contact.id);
          this.touchpointContactCtrl.setValue(contactIds);
          this.checkForDeletedContacts(touchpoint.contacts || []);
          this.updateLocationValidation(touchpoint.touchpointType);
          this.isTouchpointLoaded = true;
        });
      } else {
        this.setDefaultDatesTimes();
        this.isTouchpointLoaded = true;
      }
    });
  }

  canDeactivate(): boolean {
    return !this.touchpointForm.dirty;
  }

  selectTouchpointType(event) {
    const touchpointTypeValue = event.source._value;
    this.updateLocationValidation(touchpointTypeValue);
    this.updateRecurrenceAvailability(touchpointTypeValue);
  }

  get isTaskType(): boolean {
    return Number(this.touchpointForm.controls.touchpointType.value) === EditTouchpointComponent.TOUCHPOINT_TYPE.Task;
  }

  get isSeriesOccurrenceEdit(): boolean {
    return this.wasOriginallyRecurring && !this.applyToSeries && !this.isMergingToCrm;
  }

  get showWeekdaysError(): boolean {
    return this.recurrenceValidationAttempted
      && !this.isTaskType
      && Boolean(this.touchpointForm.controls.recurring.value)
      && this.touchpointForm.controls.recurrencePattern.value === this.recurrenceConfig.patterns.weekly.value
      && this.selectedDays.length === 0;
  }

  get showAccountManagerField(): boolean {
    if (this.isCurrentUserAccountManager) {
      return false;
    }
    return !this.isAccountDashboard || this.isMergingToCrm;
  }

  get showAccountField(): boolean {
    return !this.isAccountDashboard && !this.isOutlookOnly;
  }

  get showContactsField(): boolean {
    return !this.isOutlookOnly;
  }

  get showNotes(): boolean {
    return !this.isNewTouchpoint && !this.isMergingToCrm;
  }


  private updateRecurrenceAvailability(touchpointTypeValue: number) {
    if (touchpointTypeValue !== EditTouchpointComponent.TOUCHPOINT_TYPE.Task || !this.touchpointForm.controls.recurring.value) {
      return;
    }
    this.touchpointForm.controls.recurring.setValue(false);
    this.onRecurringChange();
  }

  private updateLocationValidation(touchpointTypeValue: number) {
    const locationControl = this.touchpointForm.controls.location;

    if (touchpointTypeValue === EditTouchpointComponent.TOUCHPOINT_TYPE.Task ||
      touchpointTypeValue === EditTouchpointComponent.TOUCHPOINT_TYPE.Call) {
      locationControl.setErrors(null);
      locationControl.setValidators([Validators.maxLength(255)]);
    } else {
      locationControl.setValidators([
        Validators.required,
        Validators.maxLength(255),
        Validators.pattern(EditTouchpointComponent.NON_WHITESPACE_PATTERN)
      ]);
    }
    locationControl.updateValueAndValidity();
  }

  /**
   * validates that all forms within the page are valid.
   */
  private formsAreValid(): boolean {
    Object.keys(this.touchpointForm.controls).forEach(key => {
      this.touchpointForm.controls[key].markAsTouched();
    });
    this.touchpointContactCtrl.markAsTouched();
    this.recurrenceValidationAttempted = true;

    const recurring = this.touchpointForm.controls.recurring.value;
    const recurrencePattern = this.touchpointForm.controls.recurrencePattern.value;
    if (recurring && !this.isSeriesOccurrenceEdit && recurrencePattern === this.recurrenceConfig.patterns.weekly.value) {
      const anyDaySelected = this.selectedDays.length > 0;
      if (!anyDaySelected) {
        return false;
      }
    }

    const contactIds = Array.isArray(this.touchpointContactCtrl.value) ? this.touchpointContactCtrl.value : [];
    const contacts = contactIds.map(id => {
      const contact = this.contactOptions.find(opt => opt.value === id);
      return contact ? { id: contact.value, name: contact.label } : null;
    }).filter(contact => contact !== null);
    this.touchpointForm.controls.contacts.setValue(contacts);

    const hasInvalidContact = this.touchpointContactCtrl.hasError('invalidContact');
    return this.touchpointForm.valid && (this.isOutlookOnly || contacts.length > 0) && !hasInvalidContact;
  }

  createTouchpoint() {
    if (this.formsAreValid()) {
      this.isLoading = true;
      const { startDate, endDate } = this.getFormattedDateTimes();
      const accountManagerEmployeeId = this.selectedAccountManagerEmployeeId;
      this.payload = {
        alarmType: Number(this.touchpointForm.controls.alarmType.value),
        contacts: this.touchpointForm.controls.contacts.value,
        startDate,
        endDate,
        location: this.touchpointForm.controls.location.value,
        notes: this.touchpointForm.controls.notes.value,
        subject: this.touchpointForm.controls.subject.value,
        touchpointType: Number(this.touchpointForm.controls.touchpointType.value),
        customerId: this.customerIdParam,
        leadId: this.leadIdParam,
        calendarId: this.touchpointIdParam || null,
        accountManagerId: this.touchpointForm.controls.accountManager.value || null,
        seriesMasterId: null,
        ...this.buildRecurrencePayload(startDate),
      };
      this.touchpointsService.saveTouchpoint(this.payload, (result: TouchpointSaveResult) => {
        this.uploadPendingAttachments(result?.touchpoint?.id, accountManagerEmployeeId, () => {
          this.isLoading = false;
          if (this.isMergingToCrm) {
            this.touchpointsService.deleteOutlookTouchpoint(this.touchpointIdParam, this.employeeId).subscribe();
          }
          this.dialogRef.close(true);
        });
      }, true, false, accountManagerEmployeeId);
    } else {
      this.notifyInvalidForm();
    }
  }

  saveTouchpoint() {
    if (!this.formsAreValid()) {
      this.notifyInvalidForm();
      return;
    }
    this.isLoading = true;
    this.addNote(() => {
      const { startDate, endDate } = this.getFormattedDateTimes();
      this.payload = {
        id: this.touchpointIdParam,
        alarmType: Number(this.touchpointForm.controls.alarmType.value),
        contacts: this.touchpointForm.controls.contacts.value,
        startDate,
        endDate,
        location: this.touchpointForm.controls.location.value,
        notes: this.touchpointForm.controls.notes.value,
        subject: this.touchpointForm.controls.subject.value,
        touchpointType: Number(this.touchpointForm.controls.touchpointType.value),
        customerId: this.customerIdParam,
        leadId: this.leadIdParam,
        accountManagerId: this.touchpointForm.controls.accountManager.value || null,
        seriesMasterId: this.seriesMasterId,
        ...this.buildRecurrencePayload(startDate),
      };
      this.touchpointsService.saveTouchpoint(this.payload, () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      }, true, this.isOutlookOnly, this.employeeId);
    });
  }

  private notifyInvalidForm() {
    this.notificationService.showNotification(this.appStrings.contacts_requiredFields, NotificationType.Error);
  }

  /**
   * Builds the recurrence-related subset of the touchpoint save payload from the current form state.
   * @param startDate the combined start date/time ISO string for this touchpoint
   */
  private buildRecurrencePayload(startDate: string): Partial<Touchpoint> {
    const isRecurring = !this.isTaskType && Boolean(this.touchpointForm.controls.recurring.value);
    if (!isRecurring) {
      return {
        isRecurring: false,
        updateRecurrence: this.applyToSeries
      };
    }

    const recurrencePattern = this.touchpointForm.controls.recurrencePattern.value;
    const selectedDayFields = this.selectedDays
      .map(day => this.recurrenceConfig.days.find(option => option.value === day)?.fieldName)
      .filter((fieldName): fieldName is RecurrenceDayField => Boolean(fieldName));
    const dayBooleans = this.recurrenceConfig.days.reduce((acc, day) => {
      acc[day.fieldName] = selectedDayFields.includes(day.fieldName);
      return acc;
    }, {} as { [key: string]: boolean });

    const localStartDate = this.combineDateTime(
      this.touchpointForm.controls.startDate.value,
      this.touchpointForm.controls.startTime.value
    );
    const recurrenceEndDate = this.touchpointForm.controls.recurrenceEndDate.value || this.minRecurrenceEndDate;

    return {
      isRecurring: true,
      updateRecurrence: !this.wasOriginallyRecurring || this.applyToSeries,
      recurrencePattern,
      recurrenceInterval: this.touchpointForm.controls.recurrenceInterval.value,
      ...dayBooleans,
      dayOfMonth: recurrencePattern === this.recurrenceConfig.patterns.absoluteMonthly.value
        ? this.touchpointForm.controls.dayOfMonth.value
        : 0,
      recurrenceStartDate: startDate,
      recurrenceEndDate,
      recurrenceStart: this.utilsService.toLocalIsoString(localStartDate),
      recurrenceEnd: recurrenceEndDate ? this.utilsService.toLocalIsoString(new Date(recurrenceEndDate)) : recurrenceEndDate,
      numberOfOccurrences: 0
    };
  }

  private getRecurrenceEndDate(value: string | Date | null | undefined): string | Date {
    return !value || this.utilsService.isMinDate(value) ? '' : value;
  }

  /**
   * Derives the selected weekday toggle values from a touchpoint's day-of-week boolean fields.
   * @param touchpoint the touchpoint to read the recurrence day flags from
   */
  private getSelectedDaysFromTouchpoint(touchpoint: Touchpoint): string[] {
    return this.recurrenceConfig.days
      .filter(day => touchpoint[day.fieldName] === true)
      .map(day => day.value);
  }

  cancel() {
    const canLeave = this.unsavedChangesGuard.canDeactivate(this);
    if (isObservable(canLeave)) {
      canLeave.subscribe(userLeaves => {
        if (userLeaves) {
          this.dialogRef.close();
        }
      });
      return;
    }
    this.dialogRef.close();
  }

  deleteTouchpoint() {
    this.deleteDialogOpen$.emit(true);
  }

  getContactMessage(): string {
    if (this.touchpointContactCtrl.hasError('invalidContact') && this.touchpointContactCtrl.touched) {
      return this.appStrings.touchpoints_selectContactFromList;
    }
    const contactsFormatted = this.touchpointContactCtrl.value || [];
    if (!contactsFormatted.length && this.deletedContacts?.length !== 0) {
      return this.appStrings.touchpoints_needValidContact;
    }
    if (!this.hasContacts && contactsFormatted.length === 0) {
      return this.appStrings.touchpoints_needContact;
    }
  }

  isContactDisabled(): boolean {
    const contactsFormatted = this.touchpointContactCtrl.value || [];
    return (
      (contactsFormatted.length === this.deletedContacts?.length && this.deletedContacts?.length !== 0) ||
      (!this.hasContacts && contactsFormatted.length === 0)
    );
  }

  private selectPendingNewContact(contacts: { id: string }[]) {
    if (!this.pendingNewContactBaseIds) {
      return;
    }
    const previousIds = this.pendingNewContactBaseIds;
    this.pendingNewContactBaseIds = null;
    const newContact = contacts.find(contact => !previousIds.has(contact.id));
    if (!newContact) {
      return;
    }
    const currentIds = Array.isArray(this.touchpointContactCtrl.value) ? this.touchpointContactCtrl.value : [];
    this.touchpointContactCtrl.setValue([...currentIds, newContact.id]);
  }

  openCreateContactDialog() {
    const customerId = this.customerIdParam || '';
    const leadId = this.leadIdParam || '';
    const customerDetails = this.customers.find(c => c.id === (customerId || leadId)) || {};

    const createContact = this.dialog.open(ContactsDashboardComponent, {
      panelClass: 'contacts-popUp',
      disableClose: true,
      data: {
        createContactDialog: true,
        customerIdDialog: customerId,
        leadIdDialog: leadId,
        customerDetails
      }
    });

    createContact.afterClosed().subscribe(dialogAnswer => {
      if (dialogAnswer !== 1) {
        return;
      }

      this.pendingNewContactBaseIds = new Set(this.contactOptions.map(option => option.value));
      if (customerId) {
        this.touchpointsService.getContactsForCustomer(customerId);
      } else if (leadId) {
        this.touchpointsService.getContactsForLead(leadId);
      }
    });
  }

  private checkForDeletedContacts(contacts) {
    this.deletedContacts = contacts.filter(contact => contact.isDeleted);

    if (this.deletedContacts.length > 0) {
      let message = this.appStrings.touchpoints_deletedContacts;
      message += this.deletedContacts.map(contact => contact.name).join(', ');
      this.notificationService.showNotification(message, NotificationType.Success);
    }
  }

  /**
   * Set default dates and times to current date/time if they don't exist
   */
  private setDefaultDatesTimes() {
    const now = new Date();
    const { startDate, endDate, startTime, endTime } = this.touchpointForm.controls;

    const prefillDate = this.utilsService.parseLocalDate(this.data?.prefillStartDate);
    const prefillStartTime = this.utilsService.applyTimeToDate(this.data?.prefillStartTime, prefillDate ?? now);
    const prefillEndTime = this.utilsService.applyTimeToDate(this.data?.prefillEndTime, prefillDate ?? now);

    if (!startDate.value) {
      startDate.setValue(prefillDate ?? now);
    }
    if (!endDate.value) {
      endDate.setValue(prefillDate ?? now);
    }

    if (!startTime.value) {
      if (prefillStartTime) {
        startTime.setValue(prefillStartTime);
      } else {
        const nextHour = new Date(now);
        nextHour.setHours(now.getHours() + 1, 0, 0, 0);
        startTime.setValue(nextHour);
      }
    }

    if (!endTime.value && startTime.value) {
      if (prefillEndTime) {
        endTime.setValue(prefillEndTime);
      } else {
        const oneHourLater = new Date(startTime.value);
        oneHourLater.setHours(oneHourLater.getHours() + 1);
        endTime.setValue(oneHourLater);
      }
    }
  }

  /**
   * Validate that end date/time is after start date/time
   */
  private validateDateTimeRelationship() {
    const startDate = this.touchpointForm.controls.startDate.value;
    const endDate = this.touchpointForm.controls.endDate.value;

    if (startDate && endDate) {
      // Normalize dates to midnight for comparison
      const startDateOnly = new Date(startDate).setHours(0, 0, 0, 0);
      const endDateOnly = new Date(endDate).setHours(0, 0, 0, 0);

      if (endDateOnly !== startDateOnly) {
        this.touchpointForm.controls.endDate.setValue(new Date(startDate), { emitEvent: false });
      }
    }

    this.ensureEndTimeAfterStartTime();
  }

  /**
   * Combine a date and time into a single Date object
   */
  private combineDateTime(date: Date, time: Date): Date {
    const combined = new Date(date);
    if (time) {
      combined.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    }
    return combined;
  }

  /**
   * Get combined start and end date/times as ISO strings
   */
  private getFormattedDateTimes(): { startDate: string, endDate: string } {
    return {
      startDate: this.combineDateTime(
        this.touchpointForm.controls.startDate.value,
        this.touchpointForm.controls.startTime.value
      ).toISOString(),
      endDate: this.combineDateTime(
        this.touchpointForm.controls.endDate.value,
        this.touchpointForm.controls.endTime.value
      ).toISOString()
    };
  }

  updateTime() {
    setTimeout(() => {
      this.ensureEndTimeAfterStartTime();
    });
  }

  private initRecurrenceDayControls() {
    this.selectedDays = [];
  }

  onDayToggle(val: { value: string }) {
    if (this.isMergingToCrm) {
      return;
    }
    const idx = this.selectedDays.indexOf(val.value);
    if (idx >= 0) {
      this.selectedDays = this.selectedDays.filter(d => d !== val.value);
    } else {
      this.selectedDays = [...this.selectedDays, val.value];
    }
  }

  private updateRecurrenceEndMinDate() {
    const startDate = this.touchpointForm.controls.startDate.value;
    this.recurrenceEndMinDate = startDate
      ? new Date(new Date(startDate).setHours(0, 0, 0, 0))
      : null;
  }

  onRecurringChange() {
    const endDateCtrl = this.touchpointForm.controls.recurrenceEndDate;
    if (this.touchpointForm.controls.recurring.value) {
      this.updateRecurrenceEndMinDate();
      endDateCtrl.setValidators([Validators.required]);
    } else {
      endDateCtrl.clearValidators();
      endDateCtrl.reset();
    }
    endDateCtrl.updateValueAndValidity();
    this.updateDayOfMonthValidators();
  }

  private updateDayOfMonthValidators() {
    const ctrl = this.touchpointForm.controls.dayOfMonth;
    const applies = !this.isSeriesOccurrenceEdit &&
      this.touchpointForm.controls.recurring.value &&
      this.touchpointForm.controls.recurrencePattern.value === this.recurrenceConfig.patterns.absoluteMonthly.value;
    if (applies) {
      ctrl.setValidators([Validators.min(1), Validators.max(31)]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private loadAccountManagers() {
    this.dashboardService.getAccountManagers((list: AccountManager[]) => {
      this.accountManagers = list ?? [];
      this.accountManagerOptions = this.accountManagers.map(am => ({ value: am.id, label: am.name }));
      this.accountManagersLoaded = true;
      this.prefillAccountManager();
    });
  }

  private prefillAccountManager() {
    const isNewOrOutlook = !this.touchpointIdParam || this.isMergingToCrm;
    const currentUserAccountManager = this.appInfoService.isAccountManager()
      ? this.accountManagers.find(am => am.employeeId === this.appInfoService.userData?.UserInfo?.EmployeeId)
      : null;
    const accountManagerId = (isNewOrOutlook && currentUserAccountManager?.id) || this.data.accountManagerId;
    if (!accountManagerId) {
      return;
    }
    this.touchpointForm.controls.accountManager.setValue(accountManagerId);
    if (this.isOutlookOnly) {
      return;
    }
    this.isLoadingCustomers = true;
    this.loadCustomers(accountManagerId, isNewOrOutlook ? this.isMergingToCrm || this.hasSelectedCustomer : true);
  }

  private get selectedAccountManagerEmployeeId(): string | undefined {
    const accountManagerId = this.touchpointForm.controls.accountManager.value;
    return this.accountManagers.find(am => am.id === accountManagerId)?.employeeId ?? this.employeeId;
  }

  get hasSelectedCustomer(): boolean {
    return Boolean(this.customerIdParam || this.leadIdParam);
  }

  get canAddContact(): boolean {
    return this.hasSelectedCustomer && this.customers.some(c => c.id === this.customerIdParam || c.id === this.leadIdParam);
  }

  private loadCustomers(accountManagerId: string, preserveValue = false) {
    const capturedValue = this.touchpointForm.controls.customerBpNumber.value;
    const employeeId = this.accountManagers.find(am => am.id === accountManagerId)?.employeeId;
    if (!employeeId) {
      this.isLoadingCustomers = false;
      return;
    }
    this.touchpointsService.getCustomersAndLeads(employeeId).subscribe((results: Customer[]) => {
      this.customers = results ?? [];
      this.customerOptions = (results ?? []).map(c => ({
        value: c.id,
        label: c.isLead ? `LEAD - ${c.name}` : c.name,
        description: [!c.isLead && c.bpNumber ? `BP#: ${c.bpNumber}` : null, c.address].filter(Boolean).join(', '),
        isLead: !!c.isLead
      }));
      this.isLoadingCustomers = false;
      this.cdr.detectChanges();
      const ctrl = this.touchpointForm.controls.customerBpNumber;
      ctrl.setValue(preserveValue ? capturedValue : null);
    });
  }

  onAccountManagerSelected(event: MatAutocompleteSelectedEvent) {
    const accountManagerId = event?.option?.value;
    if (!accountManagerId) {
      return;
    }
    this.touchpointForm.controls.accountManager.setValue(accountManagerId);
    this.touchpointForm.controls.customerBpNumber.reset();
    this.customerOptions = [];
    this.loadCustomers(accountManagerId);
  }

  onCustomerSelected(event: MatAutocompleteSelectedEvent) {
    const selectedValue = event?.option?.value;
    if (!selectedValue) {
      return;
    }
    const selectedOption = this.customerOptions.find(opt => opt.value === selectedValue);
    const isLead = selectedOption?.isLead === true;
    if (isLead) {
      this.touchpointsService.getContactsForLead(selectedValue);
      this.leadIdParam = selectedValue;
      this.customerIdParam = null;
    } else {
      this.touchpointsService.getContactsForCustomer(selectedValue);
      this.customerIdParam = selectedValue;
      this.leadIdParam = null;
    }
    this.autofillLocationFromCustomer(selectedValue, isLead);
    this.touchpointForm.controls.lastTouchpoint.setValue('');
  }

  private autofillLocationFromCustomer(customerId: string, isLead: boolean) {
    this.touchpointsService.getCustomerLocation(customerId, isLead).subscribe((location: string) => {
      this.touchpointForm.controls.location.setValue(location ?? '');
    });
  }

  triggerFileUpload() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    files.forEach(file => this.addAttachment(file));
  }

  /**
   * Attachments of a saved touchpoint are uploaded right away, the ones picked while the touchpoint
   * does not exist yet (new, or Outlook one being merged) are kept until the save creates the record.
   * @param file the file picked by the user
   */
  private addAttachment(file: File) {
    const sizeError = this.touchpointsService.getAttachmentSizeError(file);
    if (sizeError) {
      this.notificationService.showNotification(sizeError, NotificationType.Error, true);
      return;
    }
    if (!this.canUploadAttachments) {
      this.attachments = [...this.attachments, { id: null, name: file.name, contentType: file.type, size: file.size, file }];
      return;
    }
    this.isAttachmentLoading = true;
    this.touchpointsService
      .uploadAttachment(this.touchpointIdParam, file, this.applyToSeries, this.isOutlookOnly, this.employeeId)
      .subscribe({
        next: (attachmentId: string) => {
          this.isAttachmentLoading = false;
          this.attachments = [...this.attachments, {
            id: attachmentId ?? null,
            name: file.name,
            contentType: file.type,
            size: file.size
          }];
        },
        error: () => this.isAttachmentLoading = false
      });
  }

  private get canUploadAttachments(): boolean {
    return Boolean(this.touchpointIdParam) && !this.isMergingToCrm;
  }

  downloadAttachment(attachment: TouchpointAttachment) {
    if (!attachment.id) {
      return;
    }
    this.touchpointsService.downloadAttachment(attachment.id, attachment.name, this.employeeId);
  }

  removeAttachment(attachment: TouchpointAttachment) {
    if (!attachment.id) {
      this.attachments = this.attachments.filter(item => item !== attachment);
      return;
    }
    this.isAttachmentLoading = true;
    this.touchpointsService.deleteAttachment(attachment.id, this.applyToSeries, this.employeeId).subscribe({
      next: () => {
        this.isAttachmentLoading = false;
        this.attachments = this.attachments.filter(item => item.id !== attachment.id);
      },
      error: () => this.isAttachmentLoading = false
    });
  }

  /**
   * Uploads the files picked before the touchpoint existed, then hands control back to the caller
   * @param touchpointId id of the touchpoint the files belong to
   * @param employeeId employee id of the account manager whose mailbox the files are uploaded to
   * @param done invoked once every upload settled, successfully or not
   */
  private uploadPendingAttachments(touchpointId: string, employeeId: string | undefined, done: () => void) {
    const pending = this.attachments.filter(attachment => attachment.file);
    if (!touchpointId || !pending.length) {
      done();
      return;
    }
    const uploads = pending.map(attachment =>
      this.touchpointsService.uploadAttachment(touchpointId, attachment.file, this.applyToSeries, this.isOutlookOnly, employeeId));
    forkJoin(uploads).subscribe({ next: () => done(), error: () => done() });
  }

  private loadNotes() {
    this.touchpointsService.getComments(this.touchpointIdParam, this.isOutlookOnly).subscribe((notes: TouchpointComment[]) => {
      this.touchpointNotes = notes ?? [];
    });
  }

  /**
   * @param callback invoked once the typed note is added, or right away when the field is blank
   */
  addNote(callback?: () => void) {
    const detail = this.noteCtrl.value?.trim();
    if (!detail) {
      callback?.();
      return;
    }
    this.touchpointsService.createComment(this.touchpointIdParam, detail, this.applyToSeries, this.isOutlookOnly)
      .subscribe({
        next: (created: TouchpointComment) => {
          this.touchpointNotes = [...this.touchpointNotes, created];
          this.noteCtrl.reset();
          this.touchpointsService.notifyTouchpointNotesChanged();
          callback?.();
        },
        error: () => this.isLoading = false
      });
  }

  deleteNote(noteId: string) {
    this.touchpointsService.deleteComment(noteId, this.applyToSeries, this.isOutlookOnly).subscribe(() => {
      this.touchpointNotes = this.touchpointNotes.filter(n => n.id !== noteId);
      this.touchpointsService.notifyTouchpointNotesChanged();
    });
  }

  private ensureEndTimeAfterStartTime() {
    const { startDate, endDate, startTime, endTime } = this.touchpointForm.controls;

    if (!startDate.value || !endDate.value || !startTime.value || !endTime.value) {
      return;
    }

    const startDateNormalized = new Date(startDate.value).setHours(0, 0, 0, 0);
    const endDateNormalized = new Date(endDate.value).setHours(0, 0, 0, 0);

    if (startDateNormalized !== endDateNormalized) {
      return;
    }

    const startTimeMinutes = startTime.value.getHours() * 60 + startTime.value.getMinutes();
    const endTimeMinutes = endTime.value.getHours() * 60 + endTime.value.getMinutes();

    if (endTimeMinutes <= startTimeMinutes) {
      const newEndTime = new Date(startTime.value);
      const newHour = startTime.value.getHours() + 1;
      newEndTime.setHours(newHour);
      endTime.setValue(newEndTime);

      if (newHour >= 24) {
        const newEndDate = new Date(endDate.value);
        newEndDate.setDate(newEndDate.getDate() + 1);
        endDate.setValue(newEndDate, { emitEvent: false });
      }
    }
  }
}
